<template>
  <nav class="app-nav">
    <div class="nav-inner">
      <router-link to="/" class="nav-brand">
        <span class="brand-icon">🍜</span>
        <span class="brand-text">猪仔饭堂</span>
      </router-link>

      <!-- 桌面端导航链接 -->
      <div class="nav-links nav-links-desktop">
        <router-link to="/" class="nav-link" exact>
          <span class="nav-icon">🏠</span>
          <span>点菜</span>
        </router-link>
        <router-link v-if="isLoggedIn" to="/history" class="nav-link">
          <span class="nav-icon">📋</span>
          <span>历史</span>
        </router-link>
        <router-link v-if="isAdmin" to="/admin" class="nav-link">
          <span class="nav-icon">⚙️</span>
          <span>管理</span>
        </router-link>
      </div>

      <div class="nav-user">
        <button v-if="isLoggedIn" class="bell-btn" @click="openMessages" aria-label="消息中心">
          <span class="bell-icon">🔔</span>
          <span v-if="unreadCount > 0" class="bell-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
        </button>
        <router-link v-if="!isLoggedIn" to="/login" class="login-btn">
          登录
        </router-link>
        <div v-else class="user-info" @click.stop="toggleMenu">
          <span class="user-avatar">{{ currentUser.username.charAt(0) }}</span>
          <span class="user-name">{{ currentUser.username }}</span>
          <span class="role-badge" :class="currentUser.role">{{ roleLabel }}</span>
          <div v-if="showMenu" class="user-menu" @click.stop>
            <div class="menu-user-hint">切换账号请先退出</div>
            <button class="menu-item" @click="logout">退出登录</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 移动端底部导航栏 -->
    <div class="mobile-bottom-nav">
      <router-link to="/" class="mobile-nav-item" exact>
        <span class="mobile-nav-icon">🏠</span>
        <span class="mobile-nav-label">首页</span>
      </router-link>
      <router-link v-if="isLoggedIn" to="/history" class="mobile-nav-item">
        <span class="mobile-nav-icon">📋</span>
        <span class="mobile-nav-label">订单</span>
      </router-link>
      <router-link v-if="isAdmin" to="/admin" class="mobile-nav-item">
        <span class="mobile-nav-icon">⚙️</span>
        <span class="mobile-nav-label">管理</span>
      </router-link>
      <router-link v-if="!isLoggedIn" to="/login" class="mobile-nav-item">
        <span class="mobile-nav-icon">👤</span>
        <span class="mobile-nav-label">登录</span>
      </router-link>
      <button v-else class="mobile-nav-item mobile-nav-btn" @click.stop="toggleMenu">
        <span class="mobile-nav-icon">👤</span>
        <span class="mobile-nav-label">我的</span>
        <div v-if="showMenu" class="user-menu user-menu-up" @click.stop>
          <div class="menu-user-hint">{{ currentUser.username }}</div>
          <button class="menu-item" @click="logout">退出登录</button>
        </div>
      </button>
    </div>
  </nav>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'AppNav',

  data() {
    return { showMenu: false, pollTimer: null }
  },

  computed: {
    ...mapGetters('auth', ['isLoggedIn', 'isAdmin', 'currentUser']),
    ...mapGetters('messages', ['unreadCount']),
    roleLabel() {
      const map = { super_admin: '超管', admin: '管理员', member: '成员' }
      return this.currentUser ? (map[this.currentUser.role] || '') : ''
    }
  },

  mounted() {
    document.addEventListener('click', this.closeMenu)
    if (this.isLoggedIn) {
      this.startPolling()
    }
  },

  watch: {
    isLoggedIn(val) {
      if (val) {
        this.startPolling()
      } else {
        this.stopPolling()
      }
    }
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu)
    this.stopPolling()
  },

  methods: {
    toggleMenu() {
      this.showMenu = !this.showMenu
    },
    closeMenu() {
      this.showMenu = false
    },
    logout() {
      this.$store.dispatch('auth/logout')
      this.$store.dispatch('showToast', '已退出登录，请重新登录切换账号')
      this.showMenu = false
      this.$router.push('/login')
    },
    openMessages() {
      this.$store.dispatch('messages/openMessageCenter')
    },
    startPolling() {
      this.$store.dispatch('messages/fetchUnreadCount')
      this.pollTimer = setInterval(() => {
        this.$store.dispatch('messages/fetchUnreadCount')
      }, 60000)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";
@import '@/styles/mixins.scss';

.app-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: $nav-height;
  background: rgba(#fff, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid $color-border-light;
  box-shadow: $shadow-sm;
}

.nav-inner {
  @include flex-between;
  max-width: $max-width;
  margin: 0 auto;
  padding: 0 $spacing-base;
  height: 100%;
}

.nav-brand {
  @include flex-center;
  gap: $spacing-sm;
  font-weight: 700;
  font-size: $font-size-lg;
  color: $color-primary;

  .brand-icon { font-size: 22px; }
  .brand-text {
    @include mobile { display: none; }
  }
}

.nav-links-desktop {
  display: flex;
  gap: $spacing-xs;
  @include mobile { display: none; }
}

.nav-link {
  @include flex-center;
  gap: $spacing-xs;
  padding: $spacing-xs $spacing-md;
  border-radius: $radius-full;
  font-size: $font-size-sm;
  font-weight: 500;
  color: $color-text-secondary;
  transition: all $transition-base;

  .nav-icon { font-size: 16px; }

  &:hover {
    color: $color-primary;
    background: rgba($color-primary, 0.06);
  }

  &.router-link-active {
    color: $color-primary;
    background: rgba($color-primary, 0.1);
    font-weight: 600;
  }
}

// 移动端底部导航栏
.mobile-bottom-nav {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 56px;
  background: rgba(#fff, 0.96);
  backdrop-filter: blur(12px);
  border-top: 1px solid $color-border-light;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.06);
  z-index: 100;
  justify-content: space-around;
  align-items: center;
  padding: 0 $spacing-xs;

  @include mobile { display: flex; }
}

.mobile-nav-item {
  @include flex-col;
  align-items: center;
  justify-content: center;
  gap: 2px;
  flex: 1;
  text-decoration: none;
  color: $color-text-secondary;
  font-size: 0;
  padding: 6px 0;
  position: relative;
  background: none;
  border: none;
  cursor: pointer;

  &.router-link-active {
    color: $color-primary;
    .mobile-nav-icon { transform: scale(1.15); }
  }

  &:hover { color: $color-primary; }
}

.mobile-nav-icon {
  font-size: 22px;
  line-height: 1;
  transition: transform 0.2s;
}

.mobile-nav-label {
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1;
}

.user-menu-up {
  top: auto;
  bottom: calc(100% + 8px);
  right: 0;
}

.nav-user {
  @include flex-center;
  gap: $spacing-sm;
}

.bell-btn {
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: $radius-full;
  font-size: 18px;
  line-height: 1;
  transition: background 0.2s;
  &:hover { background: rgba($color-primary, 0.06); }
}

.bell-badge {
  position: absolute;
  top: 0;
  right: 0;
  background: #ef4444;
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  padding: 0 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transform: translate(30%, -30%);
}

.login-btn {
  padding: 6px 16px;
  background: $color-primary;
  color: #fff;
  border-radius: $radius-full;
  font-size: $font-size-sm;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s;
  &:hover {
    background: darken($color-primary, 8%);
  }
}

.user-info {
  position: relative;
  @include flex-center;
  gap: 6px;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: $radius-full;
  transition: background 0.2s;
  &:hover {
    background: rgba($color-primary, 0.06);
  }
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: $color-primary;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  @include flex-center;
}

.user-name {
  font-size: $font-size-sm;
  font-weight: 600;
  color: $color-text-primary;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @include mobile { display: none; }
}

.role-badge {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  &.super_admin { background: #fef3c7; color: #b45309; }
  &.admin { background: #dbeafe; color: #1d4ed8; }
  &.member { background: #f0fdf4; color: #15803d; }
  @include mobile { display: none; }
}

.user-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  min-width: 140px;
  overflow: hidden;
  z-index: 200;
}

.menu-user-hint {
  padding: 8px 16px 4px;
  font-size: 0.7rem;
  color: $color-text-muted;
  border-bottom: 1px solid $color-border-light;
}

.menu-item {
  display: block;
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  text-align: left;
  font-size: $font-size-sm;
  color: $color-text-primary;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #f5f5f5;
  }
}
</style>
